import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUploadComponent } from "../../lib";
import AddIcon from "@mui/icons-material/Add";
import DraftTextField from "../../DraftTextEditor/DraftTextField";
import ClearIcon from "@mui/icons-material/Clear";
import apiRequest from "../../../functions/api";
import { useSessionStorage } from "../../../utils/hooks";
import { LOCAL_STORAGE_KEY } from "../../../constants/localStorage";
import { useMessage } from "../../../context/message-context";

const program = "BRETTL";

const EintragErstellenDialog = ({ onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState([]);
  const [accessToken] = useSessionStorage(LOCAL_STORAGE_KEY.accessToken);
  const { showErrorMessage, showSuccessMessage } = useMessage();
  const [formValues, setFormValues] = useState({
    betreff: "",
    meldung: "",
    datei: "",
  });

  const { t } = useTranslation();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const fileName = file?.name;
    const fileData =
      file.length != 0
        ? await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result); // Base64-Daten
            reader.readAsDataURL(file); // Datei als Base64 einlesen
          })
        : null;

    // Warten auf alle Dateien
    const payload = {
      program: program,
      statement: "LADEN_AKTIV",
      formData: formValues,
      aktion: "eintragErstellen",
      dateiName: fileName,
      dateiDaten: fileData,
    };
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    apiRequest("POST", "schwarzes_brett-anfrage", payload, headers, speichernCb, errorCb);
  };

  const speichernCb = (response) => {
    handleClose();
    if (response.status === 200) {
      setFormValues({
        betreff: "",
        meldung: "",
        datei: "",
      });
      onChange();
    } else {
      if (response) {
        showErrorMessage(response.data.message);
      } else {
        showErrorMessage(t("Keine Internetverbindung"));
      }
    }
  };

  const errorCb = (error) => {
    showErrorMessage(t("Es ist ein schwerwiegender Fehler aufgetreten!"));
  };

  const handleChange = (event, newValue, newName) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues({ ...formValues, [name]: value });
    } else if (newValue?.key) {
      setFormValues({ ...formValues, [newName]: newValue?.key });
    }
  };

  const removeFile = () => {
    setFile([]);
  };

  const appendFile = (appendFile) => {
    setFile(appendFile);
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleClickOpen}
        sx={{
          borderRadius: 3,
          alignSelf: "center",
        }}
      >
        {t("Neue Meldung")}
      </Button>
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            background: (theme) =>
              `linear-gradient(
        180deg,
        ${theme.palette.primary.main}12 0%,
        transparent 100%
      )`,
            pb: 2,
          }}
        >
          {t("Neuen Eintrag erstellen")}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 1,
          }}
        >
          <DialogContentText></DialogContentText>
          <form onSubmit={handleSubmit} id="meldung-form">
            <TextField
              autoFocus
              required
              id="betreff"
              name="betreff"
              margin="normal"
              label="Betreff"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              fullWidth
              value={formValues?.betreff}
              onChange={handleChange}
            />
            <DraftTextField
              id="meldung"
              name="meldung"
              label="Meldung"
              value={formValues?.meldung}
              onChange={(text) => setFormValues({ ...formValues, ["meldung"]: text })}
              placeholder=""
              helperText=""
            ></DraftTextField>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 1,
              }}
            >
              <FileUploadComponent variant="outlined" children="Datei anhängen" color="primary" id="datei" name="datei" onChange={appendFile} />
            </Box>
            {file?.name && (
              <Box
                sx={{
                  mt: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>

                <IconButton size="small" color="error" onClick={removeFile}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </form>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
            gap: 1,
            background: (theme) =>
              `linear-gradient(
        180deg,
        ${theme.palette.primary.main}12 0%,
        transparent 100%
      )`,
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none",
            }}
          >
            {t("Abbrechen")}
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="meldung-form"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {t("Eintrag erstellen")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EintragErstellenDialog;
